import isEmpty from 'lodash/isEmpty';
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';

import { NdvrEmploymentStatus } from '@ndvr/auth-api';

export enum PersonFilterKeys {
    ndvrEmploymentStatus = 'ndvrEmploymentStatus',
    states = 'states',
    lastLoginAtStart = 'lastLoginAtStart',
    lastLoginAtEnd = 'lastLoginAtEnd',
    keywordGUIDs = 'keywordGUIDs',
}

export interface PersonFilters {
    ndvrEmploymentStatus: NdvrEmploymentStatus[];
    states: string[];
    lastLoginAtStart: string | null;
    lastLoginAtEnd: string | null;
    keywordGUIDs: string[];
}

const PERSON_FILTERS_DEFAULT: PersonFilters = {
    [PersonFilterKeys.ndvrEmploymentStatus]: [],
    [PersonFilterKeys.states]: [],
    [PersonFilterKeys.lastLoginAtStart]: null,
    [PersonFilterKeys.lastLoginAtEnd]: null,
    [PersonFilterKeys.keywordGUIDs]: [],
};

export interface PersonFiltersCtx {
    filters: PersonFilters;
    patchFilters: (partialFilters: Partial<PersonFilters>) => void;
    resetFilters: (personFilterKeys?: PersonFilterKeys[]) => void;
    isFilterActive: boolean;
    isNdvrEmploymentStatusActive: boolean;
    ndvrEmploymentStatusCount: number;
    isStatesFilterActive: boolean;
    statesFilterCount: number;
    isLastLoginAtActive: boolean;
    lastLoginAtCount: number;
    isKeywordsFilterActive: boolean;
    keywordsFilterCount: number;
}

const PersonFiltersContext = createContext<PersonFiltersCtx>({
    filters: PERSON_FILTERS_DEFAULT,
    patchFilters: () => {},
    resetFilters: () => {},
    isFilterActive: false,
    isNdvrEmploymentStatusActive: false,
    ndvrEmploymentStatusCount: 0,
    isStatesFilterActive: false,
    statesFilterCount: 0,
    isLastLoginAtActive: false,
    lastLoginAtCount: 0,
    isKeywordsFilterActive: false,
    keywordsFilterCount: 0,
});

const FILTERS_LOCAL_STORAGE_KEY = 'person_active_filters';

export const PersonFiltersContextProvider = ({ children }: PropsWithChildren) => {
    const [filters, setFilters] = useState<PersonFilters>(PERSON_FILTERS_DEFAULT);

    useEffect(() => {
        const filterStringified = globalThis.localStorage.getItem(FILTERS_LOCAL_STORAGE_KEY);
        if (filterStringified) {
            const localStorageFilters = JSON.parse(filterStringified);
            setFilters((filtersState) => ({ ...filtersState, ...localStorageFilters }));
        }
    }, []);

    useEffect(() => {
        globalThis.localStorage.setItem(FILTERS_LOCAL_STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    const patchFilters = (partialFilters: Partial<PersonFilters>) =>
        setFilters((filtersState) => ({ ...filtersState, ...partialFilters }));

    const resetFilters = (filterKeysToReset: PersonFilterKeys[] = Object.values(PersonFilterKeys)) => {
        const filterDefaults: Partial<PersonFilters> = filterKeysToReset.reduce(
            (acc, filterKey: PersonFilterKeys) => ({
                ...acc,
                [filterKey]: PERSON_FILTERS_DEFAULT[filterKey],
            }),
            {},
        );
        setFilters((filterState) => ({ ...filterState, ...filterDefaults }));
    };

    const isNdvrEmploymentStatusActive = useMemo(
        () => !isEmpty(filters.ndvrEmploymentStatus),
        [filters.ndvrEmploymentStatus],
    );

    const statesFilterCount = useMemo(() => filters.states.length, [filters.states]);

    const isStatesFilterActive = useMemo(() => 0 < statesFilterCount, [statesFilterCount]);

    const isLastLoginAtActive = useMemo(
        () => !isEmpty(filters.lastLoginAtStart) || !isEmpty(filters.lastLoginAtEnd),
        [filters.lastLoginAtStart, filters.lastLoginAtEnd],
    );

    const lastLoginAtCount = isLastLoginAtActive ? 1 : 0;

    const keywordsFilterCount = useMemo(() => filters.keywordGUIDs.length, [filters.keywordGUIDs]);

    const isKeywordsFilterActive = useMemo(() => 0 < keywordsFilterCount, [keywordsFilterCount]);

    const isFilterActive = useMemo(
        () => isNdvrEmploymentStatusActive || isStatesFilterActive || isLastLoginAtActive || isKeywordsFilterActive,
        [isNdvrEmploymentStatusActive, isStatesFilterActive, isLastLoginAtActive, isKeywordsFilterActive],
    );

    const ndvrEmploymentStatusCount = useMemo(
        () => filters.ndvrEmploymentStatus.length,
        [filters.ndvrEmploymentStatus],
    );

    return (
        <PersonFiltersContext.Provider
            value={{
                filters,
                patchFilters,
                isFilterActive,
                resetFilters,
                isNdvrEmploymentStatusActive,
                ndvrEmploymentStatusCount,
                isStatesFilterActive,
                statesFilterCount,
                isLastLoginAtActive,
                lastLoginAtCount,
                isKeywordsFilterActive,
                keywordsFilterCount,
            }}
        >
            {children}
        </PersonFiltersContext.Provider>
    );
};

export const usePersonFiltersContext = () => {
    const context = useContext(PersonFiltersContext);
    if (!context) {
        throw Error('usePersonFiltersContext must be used within PersonFiltersContextProvider');
    }
    return context;
};
